import React from 'react';
import { AiOutlineSafetyCertificate  } from "react-icons/ai";

const TimesIcon = AiOutlineSafetyCertificate  as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface ScannerData {
  transfer_fee?: {
    current_fee_rate: {
      fee_rate: number;
    };
  };
  transfer_fee_upgradable?: {
    status: string;
  };
  mintable?: {
    status: string;
  };
  freezable?: {
    status: string;
  };
  closable?: {
    status: string;
  };
  non_transferable?: string;
  metadata_mutable?:{
    status: string;
  }
  balance_mutable_authority?: {
    status: string;
  };
  transfer_hook?: any[];
  transfer_hook_upgradable?: {
    status: string;
  };
  is_open_source?: string;
  is_honeypot?: string;
  buy_tax?: string | number;
  sell_tax?: string | number;
  is_proxy?: string;
  is_mintable?: string;
  cannot_sell_all?: string;
  transfer_pausable?: string;
  is_blacklisted?: string;
  anti_whale_modifiable?: string;
  trading_cooldown?: string;
  owner_address?: string;
}

interface DataItem {
  label: string;
  value: any;
}

interface QuickAuditHeaderProps {
  scanData: any;
  chainId: any;
}

const QuickAuditHeader: React.FC<QuickAuditHeaderProps> = ({ scanData, chainId }) => {
  if (!scanData || !chainId) return null;
  
  // Cast the first scanner entry to ScannerData
  const scannerD = Object.values(scanData)[0] as ScannerData;
  let dataMap: DataItem[] = [];
  
  if (chainId === 501424) {
    if (scannerD) {
      dataMap = [
        {
          label: "Transfer Fee",
          value: scannerD.transfer_fee &&
                 Object.keys(scannerD.transfer_fee).length > 0
                   ? (scannerD.transfer_fee.current_fee_rate.fee_rate / 100) + "%"
                   : "0%"
        },
        {
          label: "Fee Update Authority",
          value: scannerD.transfer_fee_upgradable?.status === "0" ? "Revoked" : "Active"
        },
        {
          label: "Mint Authority",
          value: scannerD.mintable?.status === "0" ? "Revoked" : "Active"
        },
        {
          label: "Freeze Authority",
          value: scannerD.freezable?.status === "0" ? "Revoked" : "Active"
        },
        {
          label: "Metadata Update",
          value: scannerD.metadata_mutable?.status === "0" ? "Revoked" : "Active"
        },
        {
          label: "Closable",
          value: scannerD.closable?.status === "0" ? "No" : "Yes"
        },
        {
          label: "Honeypot",
          value: scannerD.non_transferable === "0" ? "No" : "Yes"
        },
        {
          label: "Manipulated Balance",
          value: scannerD.balance_mutable_authority?.status === "0" ? "No" : "Yes"
        },
        {
          label: "External Hooks",
          value:
            scannerD.transfer_hook &&
            scannerD.transfer_hook.length === 0 &&
            scannerD.transfer_hook_upgradable?.status === "0"
              ? "No"
              : "Yes"
        },
      ];
    }
  } else {
    // Non-Solana items
    if (scannerD) {
      dataMap = [
        {
          label: "Contract Verified",
          value: scannerD.is_open_source === "1" ? "Yes" : "No"
        },
        {
          label: "Honeypot",
          value: scannerD.is_honeypot === "1" ? "Yes" : "No"
        },
        {
          label: "Buy Tax",
          value: (scannerD.buy_tax && scannerD.buy_tax.toString().trim() !== "" ? scannerD.buy_tax : 0) + "%"
        },
        {
          label: "Sell Tax",
          value: (scannerD.sell_tax && scannerD.sell_tax.toString().trim() !== "" ? scannerD.sell_tax : 0) + "%"
        },
        {
          label: "Proxy Contract",
          value: scannerD.is_proxy === "1" ? "Yes" : "No"
        },
        {
          label: "Mintable",
          value: scannerD.is_mintable === "1" ? "Yes" : "No"
        },
        {
          label: "Max Sell Ratio",
          value: scannerD.cannot_sell_all === "1" ? "Yes" : "No"
        },
        {
          label: "Paused",
          value: scannerD.transfer_pausable === "1" ? "Yes" : "No"
        },
        {
          label: "Blacklisted",
          value: scannerD.is_blacklisted === "1" ? "Yes" : "No"
        },
        {
          label: "Antiwhale Modifiable",
          value: scannerD.anti_whale_modifiable === "1" ? "Yes" : "No"
        },
        {
          label: "Trading Cooldown",
          value: scannerD.trading_cooldown === "1" ? "Yes" : "No"
        }
      ];
    }
  }
  
  // Helper function to determine if an item is failing (red)
  const isFailing = (label: string, value: any) => {
    if (label === "Contract Verified") {
      return value !== "Yes";
    } else if (label === "Sell Tax" || label === "Buy Tax") {
      const numericValue = parseFloat(String(value).replace("%", ""));
      return numericValue >= 25;
    } else if (label === "Transfer Fee") {
      const fee = parseFloat(String(value).replace("%", ""));
      return fee > 10;
    } else if (label === "Fee Update Authority") {
      return value !== "Revoked";
    } else if (label === "Default Transaction Restriction") {
      return value !== "None";
    } else if (label === "Mint Authority") {
      return value !== "Revoked";
    } else if (label === "Freeze Authority") {
      return value !== "Revoked";
    } else if (label === "Metadata Update") {
      return value !== "Revoked";
    } else if (label === "Closable") {
      return value !== "No";
    } else if (label === "Honeypot") {
      return value !== "No";
    } else if (label === "Manipulated Balance") {
      return value !== "No";
    } else if (label === "External Hooks") {
      return value !== "No";
    } else {
      return value !== "No";
    }
  };

  const failingItems = dataMap.filter(item => isFailing(item.label, item.value));

  return (
    <div className='token-header-items'>
      {failingItems.length > 0 ? (
        <div className='issues-box'>
          <TimesIcon style={{ color: 'rgb(212 174 75)' }} /><div style={{marginRight: '3px'}}>Caution</div><div className='risk-amount medium'>{failingItems.length} issue{failingItems.length > 1 && ("s")}</div>
        </div>
      ) : (
        <div className='good-box'>
          <TimesIcon style={{ color: 'rgb(14, 203, 129)' }} /><div style={{marginRight: '3px'}}>Safe</div><div className='risk-amount low'>{failingItems.length} issues</div>
        </div>
      )}
    </div>
  );
};

export default QuickAuditHeader;
